import { mergeTheme, defaultTheme } from 'glintly-ui';

// Default color scale for reusability
const grayScale = {
  50: '#F9FAFB',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D1D5DB',
  400: '#9CA3AF',
  500: '#6B7280',
  600: '#4B5563',
  700: '#374151',
  800: '#1F2937',
  900: '#111827',
};
const infoScale = {
  25: '#EFF6FF',
  50: '#EBF5FF',
  100: '#E1EFFE',
  200: '#C3DDFD',
  300: '#A4CAFE',
  400: '#76A9FA',
  500: '#3F83F8',
  600: '#1C64F2',
  700: '#1A56DB',
  800: '#1E429F',
  900: '#233876',
};
const errorScale = {
  25: '#FFF8F1',
  50: '#FDF2F2',
  100: '#FDE8E8',
  200: '#FBD5D5',
  300: '#F8B4B4',
  400: '#F98080',
  500: '#F05252',
  600: '#E02424',
  700: '#C81E1E',
  800: '#9B1C1C',
  900: '#771D1D',
};
const warningScale = {
  25: '#FFF8F1',
  50: '#FFF8F1',
  100: '#FEECDC',
  200: '#FCD9BD',
  300: '#FDBA8C',
  400: '#FF8A4C',
  500: '#FF5A1F',
  600: '#D03801',
  700: '#B43403',
  800: '#8A2C0D',
  900: '#771D1D',
};
const successScale = {
  25: '#F3FAF7',
  50: '#F3FAF7',
  100: '#D1FAE5',
  200: '#A7F3D0',
  300: '#6EE7B7',
  400: '#34D399',
  500: '#10B981',
  600: '#059669',
  700: '#047857',
  800: '#065F46',
  900: '#064E3B',
};
const yellowScale = {
  25: '#EFF6FF',
  50: '#FDFDEA',
  100: '#FDF6B2',
  200: '#FCE96A',
  300: '#FACA15',
  400: '#E3A008',
  500: '#C27803',
  600: '#9F580A',
  700: '#8E4B10',
  800: '#723B13',
  900: '#633112',
};
const primaryScale = {
  25: '#FFF5F5',
  50: '#FEE2E2',
  100: '#FECACA',
  200: '#FCA5A5',
  300: '#F87171',
  400: '#EF4444',
  500: '#DC2626',
  600: '#B91C1C',
  700: '#991B1B',
  800: '#7F1D1D',
  900: '#450A0A',
};
const secondaryScale = {
  25: '#EFF6FF',
  50: '#EBF5FF',
  100: '#D1FAE5',
  200: '#A7F3D0',
  300: '#6EE7B7',
  400: '#34D399',
  500: '#10B981',
  600: '#059669',
  700: '#047857',
  800: '#065F46',
  900: '#064E3B',
};

/**
 * Full glintly-ui Theme (merged with library default) for barber brand colors.
 * glintly-ui ThemeProvider uses `config.themes` + `config.defaultTheme`, not `theme`.
 */
export const barberGlintlyTheme = mergeTheme(defaultTheme, {
  colors: {
    semantic: {
      primary: primaryScale,
      secondary: secondaryScale,
      accent: primaryScale,
      success: successScale,
      warning: warningScale,
      error: errorScale,
      info: infoScale,
    },
    // Flat scales required by glintly-ui Badge / legacy styled paths (e.g. n.colors.success[100]).
    primary: primaryScale,
    secondary: secondaryScale,
    success: successScale,
    warning: warningScale,
    error: errorScale,
    info: infoScale,
    gray: grayScale,
    neutral: {
      gray: grayScale,
      slate: grayScale,
    },
    background: {
      base: '#ffffff',
      surface: '#ffffff',
      elevated: '#ffffff',
      overlay: 'rgba(0, 0, 0, 0.5)',
    },
    text: {
      primary: '#0F172A',
      secondary: '#334155',
      tertiary: '#64748B',
      disabled: '#9CA3AF',
      inverse: '#ffffff',
    },
    border: {
      default: grayScale[200],
      hover: grayScale[300],
      focus: primaryScale[600],
      disabled: grayScale[200],
    },
    yellow: yellowScale,
    iconButton: grayScale,
  },
});

/** @deprecated Use barberGlintlyTheme */
export const redTheme = barberGlintlyTheme;

export const GlobalCssTheme = (theme) => {
  if (!theme || !theme.colors) {
    console.error('Theme is undefined or missing colors:', theme);
    return {};
  }

  const c = theme.colors;
  const semantic = c.semantic || {};
  const primary = semantic.primary || c.primary || primaryScale;
  const secondary = semantic.secondary || c.secondary || secondaryScale;
  const info = semantic.info || c.info || infoScale;
  const error = semantic.error || c.error || errorScale;
  const success = semantic.success || c.success || successScale;
  const warning = semantic.warning || c.warning || warningScale;
  const gray = c.neutral?.gray || c.gray || grayScale;
  const yellow = c.yellow || yellowScale;
  const iconButton = c.iconButton || gray;
  const bg =
    typeof c.background === 'string' ? c.background : (c.background?.elevated ?? '#FFFBEB');
  const surface = typeof c.surface === 'string' ? c.surface : (c.background?.surface ?? '#FFFFFF');
  const text = c.text || {};

  const fontFamilySans =
    typeof theme.typography?.fontFamily === 'string'
      ? theme.typography.fontFamily
      : theme.typography?.fontFamily?.sans ||
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

  const globalCssTheme = {
    // Primary Colors
    '--primary-25': primary?.[25] || '#FFF5F5',
    '--primary-50': primary?.[50] || '#FEE2E2',
    '--primary-100': primary?.[100] || '#FECACA',
    '--primary-200': primary?.[200] || '#FCA5A5',
    '--primary-300': primary?.[300] || '#F87171',
    '--primary-400': primary?.[400] || '#EF4444',
    '--primary-500': primary?.[500] || '#DC2626',
    '--primary-600': primary?.[600] || '#B91C1C',
    '--primary-700': primary?.[700] || '#991B1B',
    '--primary-800': primary?.[800] || '#7F1D1D',
    '--primary-900': primary?.[900] || '#450A0A',

    // Secondary Colors
    '--secondary-100': secondary?.[100] || '#D1FAE5',
    '--secondary-200': secondary?.[200] || '#A7F3D0',
    '--secondary-300': secondary?.[300] || '#6EE7B7',
    '--secondary-400': secondary?.[400] || '#34D399',
    '--secondary-500': secondary?.[500] || '#10B981',
    '--secondary-600': secondary?.[600] || '#059669',
    '--secondary-700': secondary?.[700] || '#047857',
    '--secondary-800': secondary?.[800] || '#065F46',
    '--secondary-900': secondary?.[900] || '#064E3B',

    // Info Colors
    '--info-25': info?.[25] ?? infoScale[25],
    '--info-50': info?.[50] ?? infoScale[50],
    '--info-100': info?.[100] ?? infoScale[100],
    '--info-200': info?.[200] ?? infoScale[200],
    '--info-300': info?.[300] ?? infoScale[300],
    '--info-400': info?.[400] ?? infoScale[400],
    '--info-500': info?.[500] ?? infoScale[500],
    '--info-600': info?.[600] ?? infoScale[600],
    '--info-700': info?.[700] ?? infoScale[700],
    '--info-800': info?.[800] ?? infoScale[800],
    '--info-900': info?.[900] ?? infoScale[900],

    // Error Colors
    '--error-50': error?.[50] ?? errorScale[50],
    '--error-100': error?.[100] ?? errorScale[100],
    '--error-200': error?.[200] ?? errorScale[200],
    '--error-300': error?.[300] ?? errorScale[300],
    '--error-400': error?.[400] ?? errorScale[400],
    '--error-500': error?.[500] ?? errorScale[500],
    '--error-600': error?.[600] ?? errorScale[600],
    '--error-700': error?.[700] ?? errorScale[700],
    '--error-800': error?.[800] ?? errorScale[800],
    '--error-900': error?.[900] ?? errorScale[900],

    // Success Colors
    '--success-50': success?.[50] ?? successScale[50],
    '--success-100': success?.[100] ?? successScale[100],
    '--success-200': success?.[200] ?? successScale[200],
    '--success-300': success?.[300] ?? successScale[300],
    '--success-400': success?.[400] ?? successScale[400],
    '--success-500': success?.[500] ?? successScale[500],
    '--success-600': success?.[600] ?? successScale[600],
    '--success-700': success?.[700] ?? successScale[700],
    '--success-800': success?.[800] ?? successScale[800],
    '--success-900': success?.[900] ?? successScale[900],

    // Warning Colors
    '--warning-50': warning?.[50] ?? warningScale[50],
    '--warning-100': warning?.[100] ?? warningScale[100],
    '--warning-200': warning?.[200] ?? warningScale[200],
    '--warning-300': warning?.[300] ?? warningScale[300],
    '--warning-400': warning?.[400] ?? warningScale[400],
    '--warning-500': warning?.[500] ?? warningScale[500],
    '--warning-600': warning?.[600] ?? warningScale[600],
    '--warning-700': warning?.[700] ?? warningScale[700],
    '--warning-800': warning?.[800] ?? warningScale[800],
    '--warning-900': warning?.[900] ?? warningScale[900],

    // Gray Colors
    '--gray-50': gray?.[50] ?? grayScale[50],
    '--gray-100': gray?.[100] ?? grayScale[100],
    '--gray-200': gray?.[200] ?? grayScale[200],
    '--gray-300': gray?.[300] ?? grayScale[300],
    '--gray-400': gray?.[400] ?? grayScale[400],
    '--gray-500': gray?.[500] ?? grayScale[500],
    '--gray-600': gray?.[600] ?? grayScale[600],
    '--gray-700': gray?.[700] ?? grayScale[700],
    '--gray-800': gray?.[800] ?? grayScale[800],
    '--gray-900': gray?.[900] ?? grayScale[900],

    // Yellow Colors
    '--yellow-25': yellow?.[25] ?? yellowScale[25],
    '--yellow-50': yellow?.[50] ?? yellowScale[50],
    '--yellow-100': yellow?.[100] ?? yellowScale[100],
    '--yellow-200': yellow?.[200] ?? yellowScale[200],
    '--yellow-300': yellow?.[300] ?? yellowScale[300],
    '--yellow-400': yellow?.[400] ?? yellowScale[400],
    '--yellow-500': yellow?.[500] ?? yellowScale[500],
    '--yellow-600': yellow?.[600] ?? yellowScale[600],
    '--yellow-700': yellow?.[700] ?? yellowScale[700],
    '--yellow-800': yellow?.[800] ?? yellowScale[800],
    '--yellow-900': yellow?.[900] ?? yellowScale[900],

    // Icon Button Colors
    '--iconButton-50': iconButton?.[50] ?? grayScale[50],
    '--iconButton-100': iconButton?.[100] ?? grayScale[100],
    '--iconButton-200': iconButton?.[200] ?? grayScale[200],
    '--iconButton-300': iconButton?.[300] ?? grayScale[300],
    '--iconButton-400': iconButton?.[400] ?? grayScale[400],
    '--iconButton-500': iconButton?.[500] ?? grayScale[500],
    '--iconButton-600': iconButton?.[600] ?? grayScale[600],
    '--iconButton-700': iconButton?.[700] ?? grayScale[700],
    '--iconButton-800': iconButton?.[800] ?? grayScale[800],
    '--iconButton-900': iconButton?.[900] ?? grayScale[900],

    // Base Colors
    '--background': bg,
    '--surface': surface,
    '--text-primary': text.primary || '#0F172A',
    '--text-secondary': text.secondary || '#334155',
    '--text-disabled': text.disabled || '#9CA3AF',

    // Typography
    '--font-family': fontFamilySans,
    '--font-size-xs': theme.typography?.fontSize?.xs || '0.75rem',
    '--font-size-sm': theme.typography?.fontSize?.sm || '0.875rem',
    '--font-size-md': theme.typography?.fontSize?.md || '0.875rem',
    '--font-size-lg': theme.typography?.fontSize?.lg || '1.125rem',
    '--font-size-xl': theme.typography?.fontSize?.xl || '1.25rem',
    '--font-size-2xl': theme.typography?.fontSize?.['2xl'] || '1.5rem',
    '--font-size-3xl': theme.typography?.fontSize?.['3xl'] || '1.875rem',
    '--font-size-4xl': theme.typography?.fontSize?.['4xl'] || '2.25rem',

    // Font Weights
    '--font-weight-thin': theme.typography?.fontWeight?.thin,
    '--font-weight-extralight': theme.typography?.fontWeight?.extralight,
    '--font-weight-light': theme.typography?.fontWeight?.light,
    '--font-weight-normal': theme.typography?.fontWeight?.normal,
    '--font-weight-medium': theme.typography?.fontWeight?.medium,
    '--font-weight-semibold': theme.typography?.fontWeight?.semibold,
    '--font-weight-bold': theme.typography?.fontWeight?.bold,
    '--font-weight-extrabold': theme.typography?.fontWeight?.extrabold,
    '--font-weight-black': theme.typography?.fontWeight?.black,

    // Line Heights (glintly defaults use tight/snug/normal/relaxed/loose; legacy used tighter/looser)
    '--line-height-tighter':
      theme.typography?.lineHeight?.tighter ?? theme.typography?.lineHeight?.tight,
    '--line-height-tight': theme.typography?.lineHeight?.tight,
    '--line-height-normal': theme.typography?.lineHeight?.normal,
    '--line-height-loose':
      theme.typography?.lineHeight?.loose ?? theme.typography?.lineHeight?.relaxed,
    '--line-height-looser':
      theme.typography?.lineHeight?.looser ?? theme.typography?.lineHeight?.loose,

    // Spacing
    '--spacing-xxs': theme.spacing?.xxs || '0.25rem',
    '--spacing-xs': theme.spacing?.xs || '0.5rem',
    '--spacing-sm': theme.spacing?.sm || '1rem',
    '--spacing-md': theme.spacing?.md || '1.5rem',
    '--spacing-lg': theme.spacing?.lg || '2rem',
    '--spacing-xl': theme.spacing?.xl || '3rem',
    '--spacing-2xl': theme.spacing?.['2xl'] || '4rem',

    // Border Radius
    '--border-radius-none': theme.borderRadius?.none ?? '0',
    '--border-radius-sm': theme.borderRadius?.sm ?? '0.25rem',
    '--border-radius-md': theme.borderRadius?.md ?? '0.375rem',
    '--border-radius-lg': theme.borderRadius?.lg ?? '0.5rem',
    '--border-radius-full': theme.borderRadius?.full ?? '9999px',

    // Breakpoints
    '--breakpoint-xs': theme.breakpoints?.xs ?? '320px',
    '--breakpoint-sm': theme.breakpoints?.sm ?? '640px',
    '--breakpoint-md': theme.breakpoints?.md ?? '768px',
    '--breakpoint-lg': theme.breakpoints?.lg ?? '1024px',
    '--breakpoint-xl': theme.breakpoints?.xl ?? '1280px',
    '--breakpoint-2xl': theme.breakpoints?.['2xl'] ?? '1536px',

    // Shadows
    '--shadow-sm': theme.shadows?.sm || '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    '--shadow-md':
      theme.shadows?.md || '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    '--shadow-lg':
      theme.shadows?.lg ||
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',

    // Additional variables that glintly-ui might expect
    '--color-100': gray?.[100] || '#F3F4F6',
    '--color-200': gray?.[200] || '#E5E7EB',
    '--color-300': gray?.[300] || '#D1D5DB',
    '--color-400': gray?.[400] || '#9CA3AF',
    '--color-500': gray?.[500] || '#6B7280',
    '--color-600': gray?.[600] || '#4B5563',
    '--color-700': gray?.[700] || '#374151',
    '--color-800': gray?.[800] || '#1F2937',
    '--color-900': gray?.[900] || '#111827',

    // Primary color variants that might be expected
    '--color-primary-100': primary?.[100] || '#FECACA',
    '--color-primary-200': primary?.[200] || '#FCA5A5',
    '--color-primary-300': primary?.[300] || '#F87171',
    '--color-primary-400': primary?.[400] || '#EF4444',
    '--color-primary-500': primary?.[500] || '#DC2626',
    '--color-primary-600': primary?.[600] || '#B91C1C',
    '--color-primary-700': primary?.[700] || '#991B1B',
    '--color-primary-800': primary?.[800] || '#7F1D1D',
    '--color-primary-900': primary?.[900] || '#450A0A',

    // glintly-ui control tokens (library defaults are blue)
    '--glintly-btn-primary-bg': primary?.[500] || '#DC2626',
    '--glintly-btn-primary-hover': primary?.[600] || '#B91C1C',
    '--glintly-btn-primary-text': '#ffffff',
    '--glintly-btn-outline-border': primary?.[500] || '#DC2626',
    '--glintly-btn-outline-text': primary?.[500] || '#DC2626',
    '--glintly-btn-outline-hover-bg': 'rgba(220, 38, 38, 0.08)',
    '--glintly-btn-ghost-text': primary?.[500] || '#DC2626',
    '--glintly-btn-ghost-hover-bg': 'rgba(220, 38, 38, 0.08)',
    '--glintly-btn-focus-ring': primary?.[500] || '#DC2626',
    '--glintly-input-focus': primary?.[500] || '#DC2626',
    '--glintly-modal-btn-primary-bg': primary?.[500] || '#DC2626',
    '--glintly-modal-btn-primary-hover': primary?.[600] || '#B91C1C',
    '--glintly-modal-btn-primary-active': primary?.[700] || '#991B1B',
    '--glintly-step-primary': primary?.[500] || '#DC2626',
    '--glintly-step-primary-100': 'rgba(220, 38, 38, 0.2)',
    '--glintly-carousel-dot-active': primary?.[500] || '#DC2626',
    '--glintly-carousel-dot-hover-active': primary?.[600] || '#B91C1C',
  };
  return globalCssTheme;
};
